/* @flow */

import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { KeyValuePair, Undefined } from '../../../helpers/jsHelpers/types';
import type { STREAM_PRIORITIES_TYPE, VOD_STATUS_TYPE } from '../../../helpers/ui/metadata/Types';
import type { ChannelMap } from '../../../libs/netgemLibrary/v8/types/Channel';
import type { NETGEM_API_V8_METADATA_PROGRAM, NETGEM_API_V8_METADATA_SERIES } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_API_V8_URL_DEFINITION } from '../../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { NETGEM_API_V8_RIGHTS } from '../../../libs/netgemLibrary/v8/types/Rights';
import type { NetgemApiV8WidgetItemClick, TILE_CONFIG_TYPE } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import type { RequestResponseMethodDefinitionType } from '../../../redux/netgemApi/actions/emitter';
import type { BO_FAVORITE_LIST_TYPE } from '../../../redux/netgemApi/actions/videofutur/types/favorite';
import type { CARD_DATA_MODAL_TYPE } from '../../modal/cardModal/CardModalConstsAndTypes';

import { APPLICATION_ID } from '../../../helpers/applicationCustomization/types';
import { LoadableStatus } from '../../../helpers/loadable/loadable';
import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import { isFavoriteGranted, isWishlistGranted } from '../../../helpers/rights/wishlist';
import { ExtendedItemType } from '../../../helpers/ui/item/types';
import { BroadcastStatus } from '../../../helpers/ui/location/Format';
import { getCardUrlDefinitionForItem } from '../../../helpers/ui/section/tile';
import { getTitIdFromProviderInfo, getVodLocationFromVtiId, hasProgramTrailer, hasSeriesTrailer } from '../../../helpers/videofutur/metadata';
import WishlistCache from '../../../helpers/wishlist/WishlistCache';
import { ignoreIfAborted } from '../../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import { ItemContent, ItemLocationKind, ItemType, type NETGEM_API_V8_FEED_ITEM } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { ScheduledRecordingsKind } from '../../../libs/netgemLibrary/v8/types/Npvr';
import { type NETGEM_API_V8_AUTHENT_REALM, NETGEM_API_V8_AUTHENT_REALM_VIDEOFUTUR } from '../../../libs/netgemLibrary/v8/types/Realm';
import { FEATURE_SUBSCRIPTION } from '../../../redux/appConf/constants';
import { RegistrationType } from '../../../redux/appRegistration/types/types';
import { hideModal } from '../../../redux/modal/actions';
import { addToWishlist, removeFromWishlist } from '../../../redux/netgemApi/actions/personalData/wishlist';
import addToFavoriteList from '../../../redux/netgemApi/actions/videofutur/addFavorite';
import removeFromFavoriteList from '../../../redux/netgemApi/actions/videofutur/deleteFavorite';
import { PictoBookmark, PictoHeartEmpty, PictoHeartFull, PictoTrailer } from '../../pictos/Element';
import RecordButton, { DisplayMode, ContextKind as RecordContext } from '../../recordButton/RecordButton';
import './ItemIconBar.css';

const EMPTY_FAVORITE_LIST: BO_FAVORITE_LIST_TYPE = [];

type IconBarPropType = {
  +authority?: NETGEM_API_V8_AUTHENT_REALM,
  +broadcastStatus: BroadcastStatus,
  +contentType: ItemContent,
  +gameLocationMetadata: NETGEM_API_V8_METADATA_SCHEDULE | null,
  +isVisible: boolean,
  +item: NETGEM_API_V8_FEED_ITEM,
  +onItemClick?: NetgemApiV8WidgetItemClick,
  +previewCatchupScheduledEventId: string | null,
  +programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null,
  +seriesMetadata: NETGEM_API_V8_METADATA_SERIES | null,
  +tileConfig: TILE_CONFIG_TYPE,
  +tvLocationMetadata: NETGEM_API_V8_METADATA_SCHEDULE | null,
  +vodLocationsMetadata: Array<NETGEM_API_V8_METADATA_SCHEDULE> | null,
  +vodStatus: VOD_STATUS_TYPE | null,
  +vtiId: ?number,
};

// eslint-disable-next-line max-lines-per-function
const IconBar = ({
  authority,
  broadcastStatus,
  contentType,
  gameLocationMetadata,
  isVisible,
  item,
  onItemClick,
  previewCatchupScheduledEventId,
  programMetadata,
  seriesMetadata,
  tileConfig,
  tvLocationMetadata,
  vodLocationsMetadata,
  vodStatus,
  vtiId,
}: IconBarPropType): React.Node => {
  const dispatch = useDispatch();

  const abortController = useRef<AbortController>(new AbortController());

  const favoriteList: BO_FAVORITE_LIST_TYPE = useSelector((state) => state.ui.favoriteList || EMPTY_FAVORITE_LIST);
  const wishlistStatus: LoadableStatus = useSelector((state) => state.ui.wishlistStatus);
  const wishlist: Set<string> = useSelector((state) => WishlistCache.checkGet(state.ui.wishlist, wishlistStatus).ids);
  const channels: ChannelMap = useSelector((state) => state.appConfiguration.deviceChannels);
  const commercialOffers: KeyValuePair<Array<string>> | null = useSelector((state) => state.appConfiguration.rightsCommercialOffers);
  const defaultOnItemClick: NetgemApiV8WidgetItemClick | null = useSelector((state) => (state.ui.defaultActions ? state.ui.defaultActions.onItemClick : null));
  const defaultRights: NETGEM_API_V8_RIGHTS | null = useSelector((state) => state.appConfiguration.rightsDefault);
  const isRegisteredAsGuest: boolean = useSelector((state) => state.appRegistration.registration === RegistrationType.RegisteredAsGuest);
  const isSubscriptionFeatureEnabled: boolean = useSelector((state) => state.appConfiguration.features[FEATURE_SUBSCRIPTION]);
  const streamPriorities: STREAM_PRIORITIES_TYPE | null = useSelector((state) => state.appConfiguration.playerStreamPriorities);
  const userRights: Array<string> | null = useSelector((state) => state.appConfiguration.rightsUser);

  const isFavorite = useMemo(() => {
    if (authority === NETGEM_API_V8_AUTHENT_REALM_VIDEOFUTUR) {
      if (!programMetadata) {
        return false;
      }

      const { providerInfo } = programMetadata;
      if (!providerInfo) {
        return false;
      }

      const titId = getTitIdFromProviderInfo(providerInfo, seriesMetadata);
      return favoriteList.includes(titId) ?? false;
    }

    if (wishlistStatus !== LoadableStatus.Loaded) {
      return false;
    }

    const { selectedProgramId: programId, seriesId } = item;
    return wishlist.has(seriesId || programId);
  }, [authority, favoriteList, item, programMetadata, seriesMetadata, wishlist, wishlistStatus]);

  // Only contains a cleanup function
  useEffect(() => () => abortController.current.abort('Component IconBar will unmount'), []);

  const toggleFavorite = useCallback(
    (toggler: (string, AbortSignal) => RequestResponseMethodDefinitionType) => {
      const { type: tileType } = tileConfig;
      const { signal } = abortController.current;

      if (!programMetadata) {
        return;
      }

      const { providerInfo } = programMetadata;

      if (!providerInfo) {
        return;
      }

      const locationMetadata = getVodLocationFromVtiId(vodLocationsMetadata, vodStatus?.vtiId);
      const cardData: CARD_DATA_MODAL_TYPE = {
        item,
        programMetadata,
        seriesMetadata,
        tileType,
      };

      // In case of TV item, add the program/series card URL definition (if found) so that the episodes and the related items will be displayed on the card
      const urlDefinition = getCardUrlDefinitionForItem(item, seriesMetadata, onItemClick, defaultOnItemClick);
      if (urlDefinition !== null) {
        cardData.urlDefinition = urlDefinition;
      }

      const titId = getTitIdFromProviderInfo(providerInfo, seriesMetadata);

      if (!isFavoriteGranted(isRegisteredAsGuest, isSubscriptionFeatureEnabled, cardData, titId, locationMetadata?.location, channels, commercialOffers, defaultRights, userRights)) {
        // Add to favorite list denied: log-in or subscribe page has been requested
        dispatch(hideModal());
        return;
      }

      dispatch(toggler(titId, signal)).catch((error) => ignoreIfAborted(signal, error));
    },
    [
      channels,
      commercialOffers,
      defaultOnItemClick,
      defaultRights,
      dispatch,
      isRegisteredAsGuest,
      isSubscriptionFeatureEnabled,
      item,
      onItemClick,
      programMetadata,
      seriesMetadata,
      tileConfig,
      userRights,
      vodLocationsMetadata,
      vodStatus,
    ],
  );

  const toggleWishlist = useCallback(
    (toggler: (string, string, AbortSignal) => RequestResponseMethodDefinitionType) => {
      const {
        selectedLocation: { channelId },
        selectedProgramId,
      } = item;
      const { type: tileType } = tileConfig;
      const { signal } = abortController.current;
      const assetId = seriesMetadata?.id ?? selectedProgramId;

      if (!channelId) {
        // Channel Id is null
        return;
      }

      // Select correct location metadata
      let locationMetadata = null;
      if (gameLocationMetadata) {
        locationMetadata = gameLocationMetadata;
      } else if (tvLocationMetadata) {
        locationMetadata = tvLocationMetadata;
      } else {
        locationMetadata = getVodLocationFromVtiId(vodLocationsMetadata, vtiId);
      }

      const cardData: CARD_DATA_MODAL_TYPE = {
        item,
        programMetadata,
        seriesMetadata,
        tileType,
      };

      // In case of TV item, add the program/series card URL definition (if found) so that the episodes and the related items will be displayed on the card
      const urlDefinition = getCardUrlDefinitionForItem(item, seriesMetadata, onItemClick, defaultOnItemClick);
      if (urlDefinition !== null) {
        cardData.urlDefinition = urlDefinition;
      }

      if (!isWishlistGranted(isRegisteredAsGuest, isSubscriptionFeatureEnabled, cardData, locationMetadata?.location, channels, commercialOffers, defaultRights, userRights)) {
        // Add to wishlist denied: log-in or subscribe page has been requested
        dispatch(hideModal());
        return;
      }

      dispatch(toggler(assetId, channelId, signal)).catch((error) => ignoreIfAborted(signal, error));
    },
    [
      channels,
      commercialOffers,
      defaultOnItemClick,
      defaultRights,
      dispatch,
      isRegisteredAsGuest,
      isSubscriptionFeatureEnabled,
      item,
      onItemClick,
      programMetadata,
      seriesMetadata,
      userRights,
      tvLocationMetadata,
      vodLocationsMetadata,
      vtiId,
    ],
  );

  const handleAddToWishlistButtonOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (authority === NETGEM_API_V8_AUTHENT_REALM_VIDEOFUTUR) {
        toggleFavorite((titId, signal) => addToFavoriteList(titId, signal));
      } else {
        toggleWishlist((assetId, channelId, signal) => addToWishlist(assetId, channelId, signal));
      }
    },
    [authority, toggleFavorite, toggleWishlist],
  );

  const handleRemoveFromWishlistButtonOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (authority === NETGEM_API_V8_AUTHENT_REALM_VIDEOFUTUR) {
        toggleFavorite((titId, signal) => removeFromFavoriteList(titId, signal));
      } else {
        toggleWishlist((assetId, channelId, signal) => removeFromWishlist(assetId, channelId, signal));
      }
    },
    [authority, toggleFavorite, toggleWishlist],
  );

  const pictoWishlist = useMemo(() => {
    if (process.env.REACT_APP_ID === APPLICATION_ID.FranceChannel) {
      // France Channel special case
      if (isFavorite) {
        return (
          <PictoBookmark
            aria-label={Localizer.localize('accessibility.navigation.remove_from_wishlist')}
            className='bookmark favorite'
            key='wishlist'
            onClick={handleRemoveFromWishlistButtonOnClick}
          />
        );
      }
      return <PictoBookmark aria-label={Localizer.localize('accessibility.navigation.add_to_wishlist')} className='bookmark' key='wishlist' onClick={handleAddToWishlistButtonOnClick} />;
    }

    // All other apps
    if (isFavorite) {
      return <PictoHeartFull aria-label={Localizer.localize('accessibility.navigation.remove_from_wishlist')} className='favorite' key='wishlist' onClick={handleRemoveFromWishlistButtonOnClick} />;
    }

    return <PictoHeartEmpty aria-label={Localizer.localize('accessibility.navigation.add_to_wishlist')} className='emptyHeart' key='wishlist' onClick={handleAddToWishlistButtonOnClick} />;
  }, [handleAddToWishlistButtonOnClick, handleRemoveFromWishlistButtonOnClick, isFavorite]);

  const handleTrailerButtonOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (programMetadata) {
        Messenger.emit(MessengerEvents.OPEN_PLAYER, {
          programMetadata,
          seriesMetadata,
          type: ExtendedItemType.Trailer,
        });
      }
    },
    [programMetadata, seriesMetadata],
  );

  const actions = useMemo(() => {
    const actionList = [pictoWishlist];

    if (contentType !== ItemContent.VODOrDeeplink) {
      const { locType } = item;
      const { type: tileType } = tileConfig;

      let cardDataUrlDefinition: Undefined<NETGEM_API_V8_URL_DEFINITION> = undefined;
      if (tvLocationMetadata) {
        // In case of TV item, add the program/series card URL definition (if found) so that the episodes and the related items will be displayed on the card
        const urlDefinition = getCardUrlDefinitionForItem(item, seriesMetadata, onItemClick, defaultOnItemClick);
        if (urlDefinition) {
          cardDataUrlDefinition = urlDefinition;
        }
      }

      if (broadcastStatus !== BroadcastStatus.Future && broadcastStatus !== BroadcastStatus.Unknown) {
        // Live scheduled event, catchup or recording
        actionList.push(
          <RecordButton
            broadcastStatus={broadcastStatus}
            cardDataUrlDefinition={cardDataUrlDefinition}
            context={RecordContext.Tile}
            displayMode={DisplayMode.Picto}
            item={item}
            key='recordButton'
            kind={locType === ItemLocationKind.Catchup ? ScheduledRecordingsKind.KeepFromReplay : undefined}
            location={tvLocationMetadata?.location ?? null}
            previewCatchupScheduledEventId={previewCatchupScheduledEventId}
            programMetadata={programMetadata}
            seriesMetadata={seriesMetadata}
            tileType={tileType}
          />,
        );
      }
    }

    if ((hasProgramTrailer(streamPriorities, programMetadata) && item.type === ItemType.Program) || (hasSeriesTrailer(streamPriorities, seriesMetadata) && item.type === ItemType.Series)) {
      actionList.push(<PictoTrailer aria-label={Localizer.localize('accessibility.navigation.watch_trailer')} key='trailer' onClick={handleTrailerButtonOnClick} />);
    }

    return actionList;
  }, [
    broadcastStatus,
    contentType,
    defaultOnItemClick,
    item,
    onItemClick,
    pictoWishlist,
    previewCatchupScheduledEventId,
    programMetadata,
    seriesMetadata,
    streamPriorities,
    tileConfig,
    tvLocationMetadata,
  ]);

  return (
    <div className='iconBar'>
      {isVisible
        ? actions.map((picto, index) =>
            picto ? (
              // eslint-disable-next-line react/no-array-index-key
              <div className='iconContainer' key={index}>
                {picto}
              </div>
            ) : null,
          )
        : null}
    </div>
  );
};

export default IconBar;
